
import React, { useState, useEffect } from 'react';
import { influencerService } from '../services/influencerService';
import { storage } from '../lib/storage';
import { Influencer, Order } from '../types';
import { 
  Users, 
  Plus, 
  Trash2, 
  Link as LinkIcon, 
  ExternalLink, 
  TrendingUp, 
  DollarSign, 
  Instagram, 
  Youtube, 
  Check, 
  Copy, 
  X,
  Zap,
  Award,
  Share2,
  Sparkles,
  BarChart3
} from 'lucide-react';

interface Props {
  storeId: string;
}

const AdminInfluencers: React.FC<Props> = ({ storeId }) => {
  const [influencers, setInfluencers] = useState<Influencer[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    handle: '',
    commissionRate: 10
  });

  useEffect(() => {
    loadData();
  }, [storeId]);

  const loadData = () => {
    setInfluencers(influencerService.getStoreInfluencers(storeId));
  };

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    influencerService.addInfluencer(storeId, formData.name, formData.handle, formData.commissionRate);
    setShowModal(false);
    loadData();
    setFormData({ name: '', handle: '', commissionRate: 10 });
  };

  const copyMagicLink = (id: string) => {
    const link = `${window.location.origin}?ref=${id}`;
    navigator.clipboard.writeText(link);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const totalCommissionPaid = influencers.reduce((sum, i) => sum + i.totalCommissionEarned, 0);
  const totalPartnerSales = influencers.reduce((sum, i) => sum + i.totalSalesGenerated, 0);

  return (
    <div className="space-y-10 animate-in fade-in duration-700 pb-20">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div>
          <h1 className="text-3xl font-black text-gray-900 tracking-tight">Social Commerce Hub</h1>
          <p className="text-gray-400 text-sm font-bold uppercase tracking-widest">Influencer & Creator Network</p>
        </div>
        <button 
          onClick={() => setShowModal(true)}
          className="bg-purple-600 text-white px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-purple-100 hover:bg-purple-700 transition-all flex items-center gap-2"
        >
          <Plus className="h-4 w-4" /> Recuit Creator
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
         <div className="bg-white p-8 rounded-[32px] border border-gray-100 shadow-xl flex items-center gap-6">
            <div className="w-14 h-14 bg-purple-50 text-purple-600 rounded-2xl flex items-center justify-center"><Users className="h-7 w-7" /></div>
            <div>
               <p className="text-gray-400 text-[10px] font-black uppercase tracking-widest">Active Partners</p>
               <h3 className="text-3xl font-black text-gray-900">{influencers.length}</h3>
            </div>
         </div>
         <div className="bg-white p-8 rounded-[32px] border border-gray-100 shadow-xl flex items-center gap-6">
            <div className="w-14 h-14 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center"><BarChart3 className="h-7 w-7" /></div>
            <div>
               <p className="text-gray-400 text-[10px] font-black uppercase tracking-widest">Social Sales (GMV)</p>
               <h3 className="text-3xl font-black text-gray-900">${totalPartnerSales.toLocaleString()}</h3>
            </div>
         </div>
         <div className="bg-white p-8 rounded-[32px] border border-gray-100 shadow-xl flex items-center gap-6">
            <div className="w-14 h-14 bg-amber-50 text-amber-600 rounded-2xl flex items-center justify-center"><DollarSign className="h-7 w-7" /></div>
            <div>
               <p className="text-gray-400 text-[10px] font-black uppercase tracking-widest">Commissions Owed</p>
               <h3 className="text-3xl font-black text-gray-900">${totalCommissionPaid.toLocaleString()}</h3>
            </div>
         </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
         <div className="lg:col-span-2 space-y-6">
            {influencers.length === 0 ? (
              <div className="bg-white p-24 text-center space-y-4 rounded-[40px] border border-dashed border-gray-200 opacity-50">
                 <Share2 className="h-16 w-16 mx-auto text-gray-300" />
                 <p className="font-black uppercase tracking-widest text-xs">No influencers onboarded yet. Reach out to creators to grow!</p>
              </div>
            ) : (
              <div className="bg-white rounded-[40px] border border-gray-100 shadow-xl overflow-hidden">
                 <table className="w-full text-left">
                    <thead className="bg-gray-50 text-[10px] font-black text-gray-400 uppercase tracking-widest border-b border-gray-100">
                       <tr>
                          <th className="px-8 py-5">Creator</th>
                          <th className="px-8 py-5 text-center">Comm. %</th>
                          <th className="px-8 py-5 text-right">Magic Link</th>
                          <th className="px-8 py-5 text-right">Sales</th>
                       </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                       {influencers.map(inf => (
                         <tr key={inf.id} className="group hover:bg-gray-50/50 transition-all">
                            <td className="px-8 py-6">
                               <div className="flex items-center gap-4">
                                  <div className="w-10 h-10 bg-purple-600 rounded-xl flex items-center justify-center text-white font-black">
                                     {inf.name[0]}
                                  </div>
                                  <div>
                                     <p className="font-black text-sm text-gray-900">{inf.name}</p>
                                     <p className="text-[10px] text-purple-600 font-bold">@{inf.handle}</p>
                                  </div>
                               </div>
                            </td>
                            <td className="px-8 py-6 text-center">
                               <span className="bg-purple-50 text-purple-600 text-[10px] font-black px-3 py-1 rounded-full uppercase">{inf.commissionRate}%</span>
                            </td>
                            <td className="px-8 py-6 text-right">
                               <button 
                                 onClick={() => copyMagicLink(inf.id)}
                                 className={`p-3 rounded-xl transition-all ${copiedId === inf.id ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-50 text-gray-400 hover:text-purple-600'}`}
                               >
                                  {copiedId === inf.id ? <Check className="h-4 w-4" /> : <LinkIcon className="h-4 w-4" />}
                               </button>
                            </td>
                            <td className="px-8 py-6 text-right">
                               <p className="font-black text-gray-900">${inf.totalSalesGenerated.toLocaleString()}</p>
                            </td>
                         </tr>
                       ))}
                    </tbody>
                 </table>
              </div>
            )}
         </div>

         <div className="space-y-8">
            <div className="bg-purple-600 p-10 rounded-[40px] text-white shadow-2xl relative overflow-hidden group">
               <div className="absolute top-0 right-0 p-8 opacity-20 group-hover:scale-110 transition-transform"><Sparkles className="h-24 w-24" /></div>
               <div className="relative space-y-6">
                  <h3 className="text-xl font-black tracking-tight">Creator Insights</h3>
                  <p className="text-purple-100 text-sm leading-relaxed">
                     Bhai, influencers with "Tech Unboxing" focus have a 3.4x higher conversion rate on Lynqar. Invite more YouTubers!
                  </p>
                  <button className="w-full py-4 bg-white text-purple-600 rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-xl">
                     Find Creators for Me
                  </button>
               </div>
            </div>
            
            <div className="bg-white p-8 rounded-[32px] border border-gray-100 shadow-xl space-y-4">
               <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Top Performer</h4>
               {influencers.length > 0 ? (
                 <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-amber-100 text-amber-600 rounded-2xl flex items-center justify-center"><Award className="h-6 w-6" /></div>
                    <div>
                       <p className="font-black text-gray-900">{influencers.sort((a,b) => b.totalSalesGenerated - a.totalSalesGenerated)[0].name}</p>
                       <p className="text-[10px] text-gray-400 font-bold uppercase">Leading Tech Evangelist</p>
                    </div>
                 </div>
               ) : (
                 <p className="text-xs text-gray-400 italic">No rankings yet.</p>
               )}
            </div>
         </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-[200] bg-gray-950/80 backdrop-blur-xl flex items-center justify-center p-4">
           <div className="bg-white w-full max-w-lg rounded-[40px] shadow-2xl overflow-hidden animate-in zoom-in duration-300">
              <div className="p-8 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                 <h3 className="text-2xl font-black text-gray-900 tracking-tight">Onboard Tech Creator</h3>
                 <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-full transition-all"><X className="h-6 w-6 text-gray-400" /></button>
              </div>
              <form onSubmit={handleCreate} className="p-10 space-y-8">
                 <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Creator Name</label>
                    <input required value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="e.g. Marques B." className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl font-bold" />
                 </div>
                 <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Social Handle</label>
                    <div className="relative">
                       <Instagram className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-300" />
                       <input required value={formData.handle} onChange={e => setFormData({...formData, handle: e.target.value})} placeholder="mkbhd" className="w-full pl-12 pr-6 py-4 bg-gray-50 border-none rounded-2xl font-bold" />
                    </div>
                 </div>
                 <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Commission Rate (%)</label>
                    <input type="number" value={formData.commissionRate} onChange={e => setFormData({...formData, commissionRate: Number(e.target.value)})} className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl font-bold" />
                 </div>
                 <div className="p-6 bg-purple-50 rounded-3xl border border-purple-100 flex items-start gap-4">
                    <Sparkles className="h-5 w-5 text-purple-600 shrink-0 mt-0.5" />
                    <p className="text-[10px] text-purple-700 font-medium leading-relaxed italic uppercase">
                       Magic Link will be automatically generated. Clicks and sales will be tracked for lifetime Attribution.
                    </p>
                 </div>
                 <button type="submit" className="w-full py-6 bg-purple-600 text-white rounded-[2rem] font-black text-lg shadow-2xl hover:bg-purple-700 transition-all">
                    Activate Creator Partnership
                 </button>
              </form>
           </div>
        </div>
      )}
    </div>
  );
};

export default AdminInfluencers;
